/**
 * PANNEAU TEMPORAIRE ADMIN - Comparaison Audio Coral vs Nova
 * 
 * Objectif : Permettre une validation auditive humaine du style vocal TTS
 * 
 * Ce panneau n'est PAS un correctif. C'est un outil de diagnostic.
 * La décision Coral vs Nova doit être prise après écoute réelle sur iPad/Safari.
 * 
 * AUCUNE MODIFICATION de la logique audio, du verrou anti-double voix,
 * du pause/resume, du TTS iPad Safari, ou des modules pédagogiques.
 */

import React, { useState, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Textes de test fixes - sans ponctuation dramatique
const TEST_TEXTS = {
  phrase: {
    label: "Phrase courte",
    thai: "ขอโทษค่ะ ขอชาเย็นหวานน้อยได้ไหมคะ",
    french: "Excusez-moi, puis-je avoir un thé glacé peu sucré ?"
  },
  dialogue: {
    label: "Mini-dialogue",
    thai: "ลูกค้า: ขอโทษค่ะ ขอชาเย็นหวานน้อยได้ไหมคะ\nพนักงาน: ได้ค่ะ รอสักครู่นะคะ",
    french: "Cliente: Excusez-moi, puis-je avoir un thé glacé peu sucré ?\nEmployée: Oui, un instant s'il vous plaît."
  }
};

// Configuration TTS fixe pour comparaison équitable
const TTS_CONFIG = {
  model: "tts-1",
  speed: 0.70,
  context: "phrase"  // Utilise le nettoyage anti-dramatique
};

const AudioComparePanel = () => {
  const [selectedText, setSelectedText] = useState('phrase');
  const [isPlaying, setIsPlaying] = useState(null); // 'coral' | 'nova' | null
  const [isLoading, setIsLoading] = useState(null); // 'coral' | 'nova' | null
  const [lastPlayed, setLastPlayed] = useState(null);
  const [cleanedText, setCleanedText] = useState('');
  
  const audioRef = useRef(null);

  // Fonction pour jouer l'audio avec une voix spécifique
  const playAudio = useCallback(async (voice) => {
    // Arrêter l'audio précédent
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }

    setIsLoading(voice);
    setIsPlaying(null);

    const text = TEST_TEXTS[selectedText].thai;
    
    try {
      // Construire l'URL avec les paramètres
      const params = new URLSearchParams({
        text: text,
        speed: TTS_CONFIG.speed,
        context: TTS_CONFIG.context,
        voice: voice  // coral ou nova
      });

      const response = await fetch(`${API_URL}/api/tts?${params}`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`TTS error: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      // Créer et jouer l'audio
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsPlaying(voice);
        setIsLoading(null);
      };

      audio.onended = () => {
        setIsPlaying(null);
        setLastPlayed(voice);
      };

      audio.onerror = () => {
        setIsPlaying(null);
        setIsLoading(null);
        console.error('Audio playback error');
      };

      // Récupérer le texte nettoyé depuis les logs (approximation côté client)
      setCleanedText(cleanTextForDisplay(text));

      await audio.play();
    } catch (error) {
      console.error('TTS Error:', error);
      setIsLoading(null);
    }
  }, [selectedText]);

  // Simulation du nettoyage pour affichage (le vrai nettoyage est côté serveur)
  const cleanTextForDisplay = (text) => {
    let cleaned = text;
    // Suppression des labels de dialogue
    cleaned = cleaned.replace(/[\u0E00-\u0E7F]+\s*[:：]\s*/g, '');
    // Suppression des retours à la ligne
    cleaned = cleaned.replace(/\n/g, ' ');
    return cleaned.trim();
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setIsPlaying(null);
  };

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-2xl mx-auto" data-testid="audio-compare-panel">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
        <h2 className="text-lg font-semibold text-white">
          Panneau Admin - Comparaison Audio TTS
        </h2>
      </div>

      {/* Warning */}
      <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4 mb-6">
        <p className="text-yellow-200 text-sm">
          <strong>Outil temporaire de diagnostic.</strong><br />
          Comparez Coral vs Nova à l'écoute réelle sur iPad/Safari.
          La validation du style audio doit être humaine.
        </p>
      </div>

      {/* Configuration affichée */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6 font-mono text-sm">
        <div className="grid grid-cols-2 gap-2 text-gray-300">
          <span className="text-gray-500">Modèle:</span>
          <span className="text-emerald-400">{TTS_CONFIG.model}</span>
          
          <span className="text-gray-500">Vitesse:</span>
          <span className="text-emerald-400">{TTS_CONFIG.speed}</span>
          
          <span className="text-gray-500">Contexte:</span>
          <span className="text-emerald-400">{TTS_CONFIG.context}</span>
          
          <span className="text-gray-500">Nettoyage:</span>
          <span className="text-emerald-400">anti-dramatique actif</span>
        </div>
      </div>

      {/* Sélection du texte */}
      <div className="mb-6">
        <label className="text-gray-400 text-sm mb-2 block">Texte de test :</label>
        <div className="flex gap-3">
          <button
            onClick={() => setSelectedText('phrase')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedText === 'phrase'
                ? 'bg-violet-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            data-testid="select-phrase"
          >
            Phrase courte
          </button>
          <button
            onClick={() => setSelectedText('dialogue')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedText === 'dialogue'
                ? 'bg-violet-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            data-testid="select-dialogue"
          >
            Mini-dialogue
          </button>
        </div>
      </div>

      {/* Texte affiché */}
      <div className="bg-gray-800 rounded-lg p-4 mb-6">
        <div className="mb-3">
          <span className="text-gray-500 text-xs uppercase tracking-wider">
            {TEST_TEXTS[selectedText].label}
          </span>
        </div>
        <p className="text-xl text-white mb-2 leading-relaxed" style={{ fontFamily: 'Sarabun, sans-serif' }}>
          {TEST_TEXTS[selectedText].thai}
        </p>
        <p className="text-gray-400 text-sm">
          {TEST_TEXTS[selectedText].french}
        </p>
        
        {cleanedText && selectedText === 'dialogue' && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <span className="text-gray-500 text-xs uppercase tracking-wider">
              Texte envoyé au TTS (après nettoyage)
            </span>
            <p className="text-emerald-400 mt-1 font-mono text-sm">
              {cleanedText}
            </p>
          </div>
        )}
      </div>

      {/* Boutons de test */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Bouton Coral */}
        <button
          onClick={() => isPlaying === 'coral' ? stopAudio() : playAudio('coral')}
          disabled={isLoading === 'nova'}
          className={`relative p-4 rounded-xl font-semibold transition-all ${
            isPlaying === 'coral'
              ? 'bg-orange-500 text-white ring-2 ring-orange-300'
              : isLoading === 'coral'
              ? 'bg-orange-700 text-white'
              : 'bg-orange-600 hover:bg-orange-500 text-white'
          } ${isLoading === 'nova' ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid="test-coral"
        >
          <div className="flex flex-col items-center gap-2">
            {isLoading === 'coral' ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : isPlaying === 'coral' ? (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
            <span>Tester Coral</span>
            <span className="text-xs opacity-75">(voix actuelle)</span>
          </div>
        </button>

        {/* Bouton Nova */}
        <button
          onClick={() => isPlaying === 'nova' ? stopAudio() : playAudio('nova')}
          disabled={isLoading === 'coral'}
          className={`relative p-4 rounded-xl font-semibold transition-all ${
            isPlaying === 'nova'
              ? 'bg-blue-500 text-white ring-2 ring-blue-300'
              : isLoading === 'nova'
              ? 'bg-blue-700 text-white'
              : 'bg-blue-600 hover:bg-blue-500 text-white'
          } ${isLoading === 'coral' ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid="test-nova"
        >
          <div className="flex flex-col items-center gap-2">
            {isLoading === 'nova' ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : isPlaying === 'nova' ? (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
            <span>Tester Nova</span>
            <span className="text-xs opacity-75">(alternative)</span>
          </div>
        </button>
      </div>

      {/* Dernier test */}
      {lastPlayed && (
        <div className="text-center text-gray-400 text-sm">
          Dernier test : <span className={lastPlayed === 'coral' ? 'text-orange-400' : 'text-blue-400'}>
            {lastPlayed.charAt(0).toUpperCase() + lastPlayed.slice(1)}
          </span>
        </div>
      )}

      {/* Questions de validation */}
      <div className="mt-6 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <h3 className="text-white font-medium mb-3">Questions de validation :</h3>
        <ul className="text-gray-400 text-sm space-y-2">
          <li>• Coral est-elle trop dramatique / conte pour enfants ?</li>
          <li>• Nova est-elle plus neutre et adulte ?</li>
          <li>• Nova est-elle plus naturelle et conversationnelle ?</li>
          <li>• Nova est-elle utilisable comme modèle de prononciation ?</li>
          <li>• Quelle voix préférez-vous pour l'apprentissage ?</li>
        </ul>
      </div>

      {/* Footer technique */}
      <div className="mt-6 pt-4 border-t border-gray-700 text-xs text-gray-500">
        <p>Logique audio : non modifiée | Verrou anti-double voix : non modifié | TTS iPad Safari : non modifié</p>
      </div>
    </div>
  );
};

export default AudioComparePanel;
